import {
  AltazionApiError,
  OfflineError,
  type CommerceClient,
  type ProblemDetails,
} from '@altazion/commerce-sdk-core'

declare global {
  interface Window {
    htmx?: {
      on(eventName: string, listener: (event: Event) => void): void
      off(eventName: string, listener: (event: Event) => void): void
      ajax(
        method: string,
        path: string,
        context?: {
          source?: Element
          target?: Element | string
          swap?: string
          select?: string
          values?: Record<string, unknown>
        }
      ): Promise<unknown> | void
    }
  }
}

export interface AltazionExtensionHandle {
  dispose(): void
}

export interface AltazionActionEventDetail<
  TModule extends string = string,
  TAction extends string = string,
  TResult = unknown,
> {
  module: TModule
  action: TAction
  element: Element
  payload: Record<string, unknown>
  result?: TResult
  error?: unknown
  errorDetail?: AltazionActionErrorDetail
}

export interface AltazionActionErrorDetail {
  name: string
  message: string
  status?: number
  problem?: ProblemDetails
  isOffline: boolean
  isConflict: boolean
}

export type AltazionSuccessEventKind = 'loaded' | 'updated' | 'changed' | 'none'

export interface AltazionModuleDefinition<
  TModule extends string = string,
  TAction extends string = string,
  TResult = unknown,
> {
  moduleName: TModule
  actionAttribute: string
  payloadAttributes?: Record<string, string>
  jsonPayloadAttributes?: Record<string, string>
  payloadAliases?: Record<string, string>
  normalizePayload?(payload: Record<string, unknown>): void
  parseAction(actionValue: string): TAction
  execute(client: CommerceClient, action: TAction, payload: Record<string, unknown>): Promise<TResult>
  successEventKind?: AltazionSuccessEventKind | ((action: TAction, result: TResult, payload: Record<string, unknown>) => AltazionSuccessEventKind)
  getResultEventSuffixes?(action: TAction, result: TResult, payload: Record<string, unknown>): string[]
}

interface HtmxConfigRequestEvent extends Event {
  detail: {
    headers: Record<string, string>
    elt: Element
  }
}

interface MatchedAction<TModule extends string = string, TAction extends string = string, TResult = unknown> {
  definition: AltazionModuleDefinition<TModule, TAction, TResult>
  element: Element
}

interface DisableableElement extends HTMLElement {
  disabled: boolean
}

const EXTENSION_NAME = 'altazion'
const JSON_VALUES_ATTRIBUTE = 'hx-vals'
const REFRESH_ATTRIBUTE = 'hx-altazion-refresh'
const REFRESH_EVENT_NAME = 'altazion:refresh'

const REQUEST_ATTRIBUTES = [
  { attribute: 'hx-get', method: 'GET' },
  { attribute: 'hx-post', method: 'POST' },
  { attribute: 'hx-put', method: 'PUT' },
  { attribute: 'hx-patch', method: 'PATCH' },
  { attribute: 'hx-delete', method: 'DELETE' },
] as const

export function createAltazionExtension(
  client: CommerceClient,
  modules: readonly AltazionModuleDefinition<any, any, any>[]
): AltazionExtensionHandle {
  const requestHandler = (event: Event): void => {
    const e = event as HtmxConfigRequestEvent
    e.detail.headers['Accept-Language'] = client.context.locale
    e.detail.headers['X-Altazion-Currency'] = client.context.currency
  }

  if (typeof window !== 'undefined' && window.htmx) {
    window.htmx.on('htmx:configRequest', requestHandler)
  }

  if (typeof document === 'undefined') {
    return {
      dispose(): void {
        if (typeof window !== 'undefined' && window.htmx) {
          window.htmx.off('htmx:configRequest', requestHandler)
        }
      },
    }
  }

  const handleClick = (event: Event): void => {
    const mouseEvent = event as MouseEvent
    if (
      mouseEvent.defaultPrevented
      || mouseEvent.button !== 0
      || mouseEvent.metaKey
      || mouseEvent.ctrlKey
      || mouseEvent.shiftKey
      || mouseEvent.altKey
    ) {
      return
    }

    const target = event.target
    if (!(target instanceof Element)) {
      return
    }

    const matchedAction = findMatchedAction(target, modules)
    if (
      !matchedAction
      || matchedAction.element instanceof HTMLFormElement
      || (matchedAction.element instanceof HTMLInputElement && matchedAction.element.getAttribute('hx-altazion-cart-action') === 'updateItem')
      || !hasAltazionExtension(matchedAction.element)
    ) {
      return
    }

    event.preventDefault()
    void runModuleAction(client, matchedAction)
  }

  const quantityTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>()
  const runQuantityAction = (element: Element): void => {
    const existingTimer = quantityTimers.get(element)
    if (existingTimer) {
      clearTimeout(existingTimer)
      quantityTimers.delete(element)
    }

    const matchedAction = findMatchedAction(element, modules)
    if (!matchedAction || matchedAction.element !== element || !hasAltazionExtension(element)) {
      return
    }

    const quantityInput = element as HTMLInputElement
    if (quantityInput.value.trim() === '') {
      return
    }

    element.setAttribute('hx-altazion-cart-quantity', quantityInput.value)
    void runModuleAction(client, matchedAction)
  }

  const handleQuantityInput = (event: Event): void => {
    const target = event.target
    if (!(target instanceof HTMLInputElement) || target.getAttribute('hx-altazion-cart-action') !== 'updateItem') {
      return
    }

    if (target.value.trim() === '') {
      const existingTimer = quantityTimers.get(target)
      if (existingTimer) {
        clearTimeout(existingTimer)
        quantityTimers.delete(target)
      }
      return
    }

    target.setAttribute('hx-altazion-cart-quantity', target.value)
    const timer = setTimeout(() => runQuantityAction(target), 1_000)
    quantityTimers.set(target, timer)
  }

  const handleQuantityBlur = (event: Event): void => {
    const target = event.target
    if (target instanceof HTMLInputElement && target.getAttribute('hx-altazion-cart-action') === 'updateItem') {
      if (target.value.trim() === '') {
        target.value = target.getAttribute('hx-altazion-cart-quantity') ?? ''
        return
      }
      runQuantityAction(target)
    }
  }

  const handleSubmit = (event: Event): void => {
    const target = event.target
    if (!(target instanceof HTMLFormElement)) {
      return
    }

    const matchedAction = findMatchedAction(target, modules)
    if (!matchedAction || matchedAction.element !== target || !hasAltazionExtension(target)) {
      return
    }

    event.preventDefault()
    void runModuleAction(client, matchedAction)
  }

  document.addEventListener('click', handleClick)
  document.addEventListener('submit', handleSubmit, true)
  document.addEventListener('input', handleQuantityInput)
  document.addEventListener('blur', handleQuantityBlur, true)

  return {
    dispose(): void {
      if (typeof window !== 'undefined' && window.htmx) {
        window.htmx.off('htmx:configRequest', requestHandler)
      }

      document.removeEventListener('click', handleClick)
      document.removeEventListener('submit', handleSubmit, true)
      document.removeEventListener('input', handleQuantityInput)
      document.removeEventListener('blur', handleQuantityBlur, true)
    },
  }
}

export function requireString(value: unknown, fieldName: string): string {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim()
  }

  throw new Error(`Missing payload field: ${fieldName}`)
}

export function requireNumber(value: unknown, fieldName: string): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    if (Number.isFinite(parsed)) {
      return parsed
    }
  }

  throw new Error(`Invalid payload field: ${fieldName}`)
}

export function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined
  }

  return value as Record<string, unknown>
}

async function runModuleAction<TModule extends string, TAction extends string, TResult>(
  client: CommerceClient,
  matchedAction: MatchedAction<TModule, TAction, TResult>
): Promise<void> {
  const { definition, element } = matchedAction
  const action = readActionName(element, definition)
  const payload = collectPayload(element, definition)
  const releasePending = markPending(element)

  dispatchModuleEvent<TModule, TAction, undefined>(element, buildModuleEventName(definition.moduleName, 'before'), {
    module: definition.moduleName,
    action,
    element,
    payload,
  })

  try {
    const result = await definition.execute(client, action, payload)
    const quantityAdjusted = definition.moduleName === 'cart'
      && action === 'updateItem'
      && isCartQuantityAdjusted(result, payload)

    if (quantityAdjusted) {
      const message = "La quantit\u00e9 demand\u00e9e n'est pas enti\u00e8rement disponible. La quantit\u00e9 a \u00e9t\u00e9 ajust\u00e9e au stock disponible."
      dispatchModuleEvent<TModule, TAction, undefined>(element, buildModuleEventName(definition.moduleName, 'error'), {
        module: definition.moduleName,
        action,
        element,
        payload,
        errorDetail: {
          name: 'CartQuantityAdjusted',
          message,
          isOffline: false,
          isConflict: false,
        },
      })
    }

    dispatchModuleEvent<TModule, TAction, TResult>(element, buildModuleEventName(definition.moduleName, 'after'), {
      module: definition.moduleName,
      action,
      element,
      payload,
      result,
    })

    const successEventKind = resolveSuccessEventKind(definition, action, result, payload)
    const emittedSuffixes = new Set<string>()

    if (!quantityAdjusted && successEventKind !== 'none') {
      emittedSuffixes.add(successEventKind)
      dispatchModuleEvent<TModule, TAction, TResult>(element, buildModuleEventName(definition.moduleName, successEventKind), {
        module: definition.moduleName,
        action,
        element,
        payload,
        result,
      })
    }

    for (const suffix of quantityAdjusted ? [] : definition.getResultEventSuffixes?.(action, result, payload) ?? []) {
      if (emittedSuffixes.has(suffix)) {
        continue
      }

      dispatchModuleEvent<TModule, TAction, TResult>(element, buildModuleEventName(definition.moduleName, suffix), {
        module: definition.moduleName,
        action,
        element,
        payload,
        result,
      })
    }

    await refreshTargets(element)
    if (quantityAdjusted) {
      const lineStatus = element.closest('[data-line-id]')?.querySelector<HTMLElement>('[data-cart-role="quantity-status"]')
      if (lineStatus) {
        lineStatus.textContent = "La quantit\u00e9 demand\u00e9e n'est pas enti\u00e8rement disponible. La quantit\u00e9 a \u00e9t\u00e9 ajust\u00e9e au stock disponible."
        lineStatus.hidden = false
        lineStatus.classList.remove('is-success')
        lineStatus.classList.add('is-error')
      }

      const status = element.closest('[data-altazion-cart-page-client="true"]')?.querySelector<HTMLElement>('[data-cart-role="status"]')
        ?? document.querySelector<HTMLElement>('[data-cart-role="status"]')
      if (status) {
        status.textContent = "La quantit\u00e9 demand\u00e9e n'est pas enti\u00e8rement disponible. La quantit\u00e9 a \u00e9t\u00e9 ajust\u00e9e au stock disponible."
        status.hidden = false
        status.classList.remove('is-success')
        status.classList.add('is-error')
      }
    }
  } catch (error) {
    const errorDetail = normalizeError(error)

    dispatchModuleEvent<TModule, TAction, undefined>(element, buildModuleEventName(definition.moduleName, 'error'), {
      module: definition.moduleName,
      action,
      element,
      payload,
      error,
      errorDetail,
    })
    if (definition.moduleName === 'cart' && (action === 'applyCoupon' || action === 'removeCoupon')) {
      const message = errorDetail.problem?.detail ?? errorDetail.message
      const status = element.closest('[data-altazion-cart-page-client="true"]')?.querySelector<HTMLElement>('[data-cart-role="coupon-status"]')
        ?? document.querySelector<HTMLElement>('[data-cart-role="coupon-status"]')
      if (status) {
        status.textContent = message
        status.hidden = false
        status.classList.remove('is-success')
        status.classList.add('is-error')
      }
    }
    if (definition.moduleName === 'cart' && action === 'updateItem') {
      const status = element.closest('[data-line-id]')?.querySelector<HTMLElement>('[data-cart-role="quantity-status"]')
      if (status) {
        status.textContent = errorDetail.problem?.detail ?? errorDetail.message
        status.hidden = false
        status.classList.add('is-error')
      }
    }
    console.error(`[AltazionHtmx] ${definition.moduleName} action failed`, error)
  } finally {
    releasePending()
  }
}

function findMatchedAction(
  target: Element,
  modules: readonly AltazionModuleDefinition<any, any, any>[]
): MatchedAction | null {
  let current: Element | null = target

  while (current) {
    for (const definition of modules) {
      if (current.hasAttribute(definition.actionAttribute)) {
        return { definition, element: current }
      }
    }

    current = current.parentElement
  }

  return null
}

function readActionName<TModule extends string, TAction extends string, TResult>(
  element: Element,
  definition: AltazionModuleDefinition<TModule, TAction, TResult>
): TAction {
  const action = element.getAttribute(definition.actionAttribute)
  if (!action) {
    throw new Error(`Missing ${definition.actionAttribute} attribute`)
  }

  return definition.parseAction(action)
}

function collectPayload<TModule extends string, TAction extends string, TResult>(
  element: Element,
  definition: AltazionModuleDefinition<TModule, TAction, TResult>
): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  const form = element instanceof HTMLFormElement ? element : element.closest('form')

  if (form) {
    Object.assign(payload, formDataToObject(new FormData(form)))
    mergeJsonValues(payload, form)
    applyAttributePayload(payload, form, definition.payloadAttributes)
    applyJsonAttributePayload(payload, form, definition.jsonPayloadAttributes)
  }

  mergeJsonValues(payload, element)
  applyAttributePayload(payload, element, definition.payloadAttributes)
  applyJsonAttributePayload(payload, element, definition.jsonPayloadAttributes)
  applyPayloadAliases(payload, definition.payloadAliases)
  definition.normalizePayload?.(payload)

  return payload
}

function mergeJsonValues(payload: Record<string, unknown>, element: Element): void {
  const values = readJsonValues(element, JSON_VALUES_ATTRIBUTE)
  if (values) {
    Object.assign(payload, values)
  }
}

function applyAttributePayload(
  payload: Record<string, unknown>,
  element: Element,
  attributes?: Record<string, string>
): void {
  if (!attributes) {
    return
  }

  for (const [attributeName, payloadKey] of Object.entries(attributes)) {
    const value = element.getAttribute(attributeName)
    if (value !== null) {
      payload[payloadKey] = value
    }
  }
}

function applyJsonAttributePayload(
  payload: Record<string, unknown>,
  element: Element,
  attributes?: Record<string, string>
): void {
  if (!attributes) {
    return
  }

  for (const [attributeName, payloadKey] of Object.entries(attributes)) {
    const value = readJsonValues(element, attributeName)
    if (value !== undefined) {
      payload[payloadKey] = value
    }
  }
}

function applyPayloadAliases(payload: Record<string, unknown>, aliases?: Record<string, string>): void {
  if (!aliases) {
    return
  }

  for (const [alias, canonicalKey] of Object.entries(aliases)) {
    if (payload[alias] !== undefined && payload[canonicalKey] === undefined) {
      payload[canonicalKey] = payload[alias]
    }
  }
}

function formDataToObject(formData: FormData): Record<string, unknown> {
  const payload: Record<string, unknown> = {}

  for (const [key, value] of formData.entries()) {
    const normalizedValue = typeof value === 'string' ? value : value.name
    const previous = payload[key]

    if (previous === undefined) {
      payload[key] = normalizedValue
    } else if (Array.isArray(previous)) {
      previous.push(normalizedValue)
    } else {
      payload[key] = [previous, normalizedValue]
    }
  }

  return payload
}

function dispatchModuleEvent<TModule extends string, TAction extends string, TResult>(
  element: Element,
  eventName: string,
  detail: AltazionActionEventDetail<TModule, TAction, TResult>
): void {
  element.dispatchEvent(new CustomEvent<AltazionActionEventDetail<TModule, TAction, TResult>>(eventName, {
    bubbles: true,
    detail,
  }))
}

function buildModuleEventName(moduleName: string, suffix: string): string {
  return `altazion:${moduleName}:${suffix}`
}

function resolveSuccessEventKind<TModule extends string, TAction extends string, TResult>(
  definition: AltazionModuleDefinition<TModule, TAction, TResult>,
  action: TAction,
  result: TResult,
  payload: Record<string, unknown>
): AltazionSuccessEventKind {
  const configuredKind = definition.successEventKind
  if (typeof configuredKind === 'function') {
    return configuredKind(action, result, payload)
  }

  if (configuredKind) {
    return configuredKind
  }

  return inferSuccessEventKind(action, result)
}

function inferSuccessEventKind<TAction extends string, TResult>(action: TAction, result: TResult): AltazionSuccessEventKind {
  if (result === undefined) {
    return 'changed'
  }

  const normalizedAction = action.toLowerCase()
  if (
    normalizedAction.startsWith('get')
    || normalizedAction.startsWith('find')
    || normalizedAction.startsWith('search')
    || normalizedAction.startsWith('suggest')
    || normalizedAction.startsWith('load')
    || normalizedAction.startsWith('list')
  ) {
    return 'loaded'
  }

  return 'updated'
}

function normalizeError(error: unknown): AltazionActionErrorDetail {
  if (error instanceof AltazionApiError) {
    return {
      name: error.name,
      message: error.message,
      status: error.status,
      problem: error.problem,
      isOffline: false,
      isConflict: error.status === 409,
    }
  }

  if (error instanceof OfflineError) {
    return {
      name: error.name,
      message: error.message,
      isOffline: true,
      isConflict: false,
    }
  }

  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      isOffline: false,
      isConflict: false,
    }
  }

  return {
    name: 'UnknownError',
    message: 'Unknown error',
    isOffline: false,
    isConflict: false,
  }
}

async function refreshTargets(element: Element): Promise<void> {
  const targets = resolveRefreshTargets(element)
  if (targets.length === 0) {
    return
  }

  await Promise.all(targets.map((target) => refreshTargetElement(element, target)))
}

function resolveRefreshTargets(element: Element): Element[] {
  if (typeof document === 'undefined') {
    return []
  }

  const selectors = collectRefreshSelectors(element)
  if (selectors.length === 0) {
    return []
  }

  const targets = new Set<Element>()

  for (const selector of selectors) {
    try {
      for (const target of document.querySelectorAll(selector)) {
        targets.add(target)
      }
    } catch (error) {
      console.error(`[AltazionHtmx] invalid refresh selector: ${selector}`, error)
    }
  }

  return [...targets]
}

function collectRefreshSelectors(element: Element): string[] {
  const values = new Set<string>()
  const form = element instanceof HTMLFormElement ? element : element.closest('form')

  addRefreshSelectors(values, form)
  addRefreshSelectors(values, element)

  return [...values]
}

function addRefreshSelectors(values: Set<string>, element: Element | null): void {
  const rawValue = element?.getAttribute(REFRESH_ATTRIBUTE)
  if (!rawValue) {
    return
  }

  for (const selector of rawValue.split(';')) {
    const trimmed = selector.trim()
    if (trimmed.length > 0) {
      values.add(trimmed)
    }
  }
}

async function refreshTargetElement(sourceElement: Element, targetElement: Element): Promise<void> {
  const request = readRefreshRequest(targetElement)
  if (request && typeof window !== 'undefined' && window.htmx?.ajax) {
    const context: {
      source: Element
      target: Element
      swap?: string
      select?: string
      values?: Record<string, unknown>
    } = {
      source: sourceElement,
      target: targetElement,
    }

    const swap = targetElement.getAttribute('hx-swap')
    if (swap) {
      context.swap = swap
    }

    const select = targetElement.getAttribute('hx-select')
    if (select) {
      context.select = select
    }

    const values = readJsonValues(targetElement, JSON_VALUES_ATTRIBUTE)
    if (values) {
      context.values = values
    }

    await Promise.resolve(window.htmx.ajax(request.method, request.path, context))
    return
  }

  targetElement.dispatchEvent(new CustomEvent(REFRESH_EVENT_NAME, {
    bubbles: true,
    detail: {
      source: sourceElement,
      target: targetElement,
    },
  }))
}

function readRefreshRequest(element: Element): { method: string, path: string } | null {
  for (const requestAttribute of REQUEST_ATTRIBUTES) {
    const path = element.getAttribute(requestAttribute.attribute)
    if (path) {
      return {
        method: requestAttribute.method,
        path,
      }
    }
  }

  return null
}

function readJsonValues(element: Element, attributeName: string): Record<string, unknown> | undefined {
  const raw = element.getAttribute(attributeName)
  if (!raw) {
    return undefined
  }

  try {
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    throw new Error(`Invalid JSON in ${attributeName}`)
  }

  return undefined
}

function markPending(element: Element): () => void {
  const previousBusy = element.getAttribute('aria-busy')
  element.setAttribute('aria-busy', 'true')

  const disableable = isDisableableElement(element) ? element : null
  const previousDisabled = disableable?.disabled ?? false
  if (disableable) {
    disableable.disabled = true
  }

  return (): void => {
    if (previousBusy === null) {
      element.removeAttribute('aria-busy')
    } else {
      element.setAttribute('aria-busy', previousBusy)
    }

    if (disableable) {
      disableable.disabled = previousDisabled
    }
  }
}

function isDisableableElement(element: Element): element is DisableableElement {
  return 'disabled' in element
}

function hasAltazionExtension(element: Element): boolean {
  let current: Element | null = element
  while (current) {
    const extensions = current.getAttribute('hx-ext')
    if (extensions && parseExtensions(extensions).includes(EXTENSION_NAME)) {
      return true
    }

    current = current.parentElement
  }

  return false
}

function parseExtensions(value: string): string[] {
  return value
    .split(/[\s,]+/)
    .map((extension) => extension.trim())
    .filter((extension) => extension.length > 0)
}

function isCartQuantityAdjusted(result: unknown, payload: Record<string, unknown>): boolean {
  if (!result || typeof result !== 'object' || !Array.isArray((result as { content?: unknown }).content)) {
    return false
  }

  const requestedQuantity = Number(payload.quantity)
  const lineId = String(payload.lineId ?? '')
  if (!Number.isFinite(requestedQuantity) || !lineId) {
    return false
  }

  const contents = (result as { content: Array<{ lines?: Array<{ id?: string; quantity?: number }> }> }).content
  const line = contents.flatMap((content) => content.lines ?? []).find((candidate) => candidate.id === lineId)
  return line?.quantity !== undefined && line.quantity !== requestedQuantity
}